import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://qsconn.com';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  author = "Qingshun Electronics",
  url,
  image,
  type = "website",
  publishedTime,
  modifiedTime,
  section,
  tags,
  wordCount,
  canonicalUrl,
  // Structured data extras
  products = [],
  faqs = [],
  showLocalBusiness = false,
  showOrganization = true,
  breadcrumbs = [],
  language = 'en',
  // Extra arbitrary structured data object or array
  extraStructuredData = null,
  extraStructuredDataArray = []
}) => {
  const defaultTitle = "Qingshun Electronics - Premium Connector Alternatives | 50-70% Cost Savings";
  const defaultDescription = "Leading manufacturer of high-quality connector alternatives for automotive, industrial, and electronics applications. Save 50-70% compared to major brands like TE, Molex, Amphenol with same quality standards.";
  const defaultImage = `${SITE_URL}/logo512.png`;
  
  const pageTitle = title ? `${title} | Qingshun Electronics` : defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageImage = image 
    ? (image.startsWith('http') ? image : `${SITE_URL}${image}`) 
    : defaultImage;
  const pageUrl = url 
    ? (url.startsWith('http') ? url : `${SITE_URL}${url}`) 
    : SITE_URL;
  const pageKeywords = keywords 
    ? `${keywords}, connector alternatives, electronics manufacturing, cost savings, automotive connectors, industrial connectors, TE alternative, Molex alternative` 
    : "connector alternatives, electronics manufacturing, cost savings, quality connectors, automotive connectors, medical connectors";
  
  // Language/locale mapping
  const languageMap = { 'en': 'en', 'zh': 'zh-CN', 'es': 'es' };
  const localeMap = { 'en': 'en_US', 'zh': 'zh_CN', 'es': 'es_ES' };
  const currentLang = languageMap[language] || 'en';
  const ogLocale = localeMap[language] || 'en_US';
  
  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Qingshun Electronics Technology Co., Ltd.",
    "alternateName": "Qingshun Electronics",
    "url": SITE_URL,
    "logo": {
      "@type": "ImageObject",
      "url": `${SITE_URL}/logo512.png`,
      "width": 512,
      "height": 512
    },
    "description": "Professional manufacturer of high-quality connector alternatives with 50-70% cost savings",
    "foundingDate": "2010",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "2526 Huayuan Road, Zhangpu Town",
      "addressLocality": "Kunshan City",
      "addressRegion": "Jiangsu Province",
      "addressCountry": "CN"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "tony@qsconn.com",
      "contactType": "sales",
      "availableLanguage": ["English", "Chinese", "Spanish"]
    }
  };

  // LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "Manufacturer",
    "name": "Qingshun Electronics Technology Co., Ltd.",
    "image": `${SITE_URL}/logo512.png`,
    "url": SITE_URL,
    "email": "tony@qsconn.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "2526 Huayuan Road, Zhangpu Town",
      "addressLocality": "Kunshan City",
      "addressRegion": "Jiangsu Province",
      "addressCountry": "CN"
    },
    "priceRange": "$$",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "150"
    }
  };

  // FAQ Schema
  const faqSchema = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  // Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url.startsWith('http') ? crumb.url : `${SITE_URL}${crumb.url}`
    }))
  } : null;

  // WebPage Schema (for non-articles)
  const webPageSchema = type !== "article" ? {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle,
    "description": pageDescription,
    "url": pageUrl,
    "inLanguage": currentLang
  } : null;

  // Product schema for product pages
  const productSchemas = products.length > 0 ? products.map(product => ({
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": product.image ? (product.image.startsWith('http') ? product.image : `${SITE_URL}${product.image}`) : defaultImage,
    "brand": {
      "@type": "Brand",
      "name": "Qingshun Electronics"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "url": pageUrl,
      "seller": {
        "@type": "Organization",
        "name": "Qingshun Electronics Technology Co., Ltd."
      }
    }
  })) : [];

  // Collect all extra schemas
  const allExtraSchemas = [
    ...(extraStructuredData ? [extraStructuredData] : []),
    ...extraStructuredDataArray
  ];
  
  return (
    <Helmet>
      {/* Primary meta tags */}
      <html lang={currentLang} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={canonicalUrl || pageUrl} />
      
      {/* Hreflang tags — only include when a language-specific URL is available */}
      {url && <link rel="alternate" hrefLang="en" href={`${SITE_URL}${url}`} />}
      {url && <link rel="alternate" hrefLang="zh-CN" href={`${SITE_URL}${url}`} />}
      {url && <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${url}`} />}
      
      {/* Performance hints */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === "article" ? "article" : "website"} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || "Qingshun Electronics"} />
      <meta property="og:site_name" content="Qingshun Electronics" />
      <meta property="og:locale" content={ogLocale} />
      
      {/* Article specific OG tags */}
      {type === "article" && (
        <>
          <meta property="article:published_time" content={publishedTime} />
          <meta property="article:modified_time" content={modifiedTime || publishedTime} />
          <meta property="article:author" content={author} />
          {section && <meta property="article:section" content={section} />}
          {tags && tags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      <meta name="twitter:image:alt" content={title || "Qingshun Electronics"} />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="theme-color" content="#dc2626" />
      
      {/* Mobile optimization */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      
      {/* Organization Schema */}
      {showOrganization && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}
      
      {/* LocalBusiness Schema */}
      {showLocalBusiness && (
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      )}
      
      {/* WebPage Schema */}
      {webPageSchema && (
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      )}
      
      {/* FAQ Schema */}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      
      {/* Breadcrumb Schema */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {/* Product Schemas */}
      {productSchemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {/* Extra Structured Data */}
      {allExtraSchemas.map((schema, i) => (
        <script key={`extra-${i}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
